import { Table } from "@/config/antd";
import "@/assets/styles/custom_table.css";
import PropTypes from "prop-types";
import Column from "antd/es/table/Column";
import { Link } from "react-router-dom";
import Button from "@/components/common/Button";
import DocumentCodeIcon from "@/assets/icons/web_Icons/document-code.svg";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Delete from "@/components/common/alerts/Delete";
import Success from "@/components/common/alerts/Success";
import {
  updateTicketStatusAsync,
  selectStatusUpdateLoading,
  selectStatusUpdateSuccess,
  clearStatusUpdateSuccess,
} from "@/store/redux/slices/support/support_slice";
import { useLoader } from "@/hooks/LoaderProvider";
import { selectSearchTerm } from "@/store/redux/slices/search/search_slice";

const SupportHomeTable = ({
  setSelectedEvents,
  data,
  isLoading,
}) => {
  const dispatch = useDispatch();
  const { showLoader, hideLoader } = useLoader();
  const globalSearchTerm = useSelector(selectSearchTerm) || "";

  const statusUpdateLoading = useSelector(selectStatusUpdateLoading);
  const statusUpdateSuccess = useSelector(selectStatusUpdateSuccess);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentTicket, setCurrentTicket] = useState(null);

  // Return null if there's no data
  if (!data || data.length === 0) {
    return null;
  }

  // Apply global search filter
  const filteredData = globalSearchTerm
    ? data.filter(
        (item) =>
          item.description
            ?.toLowerCase()
            .includes(globalSearchTerm.toLowerCase()) ||
          item.ticket_id
            ?.toLowerCase()
            .includes(globalSearchTerm.toLowerCase()) ||
          item.status?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
          item.type?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
          (item.date &&
            item.date.toLowerCase().includes(globalSearchTerm.toLowerCase()))
      )
    : data;

  // rowSelection object indicates the need for row selection
  const rowSelection = {
    onChange: (selectedRowKeys) => {
      setSelectedEvents(selectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: record.name === "Disabled User",
      // Column configuration not to be checked
      name: record.name,
    }),
  };

  const handleCloseClick = (ticket) => {
    setCurrentTicket(ticket);
    setShowDeleteModal(true);
  };

  const handleConfirmClose = async () => {
    try {
      showLoader();
      setShowDeleteModal(false);

      const ticketId = currentTicket._id || currentTicket.id;

      await dispatch(
        updateTicketStatusAsync({
          id: ticketId,
          status: "Closed",
        })
      ).unwrap();
    } catch (error) {
      console.error("Error closing ticket:", error);
      // Consider showing an error message to the user here
    } finally {
      hideLoader();
    }
  };

  // Simplified - just clear the success state without triggering new fetch
  const handleCloseSuccess = () => {
    dispatch(clearStatusUpdateSuccess());
    // No need to fetch data again - the parent component will handle refreshes
  };

  return (
    <>
      <Table
        rowSelection={{
          type: "checkbox",
          ...rowSelection,
        }}
        dataSource={filteredData}
        pagination={false}
        locale={{ emptyText: " " }} // Hide default empty state
        rowKey="id"
        loading={statusUpdateLoading || isLoading}>
        <Column
          title="Ticket Information"
          dataIndex="title"
          key="title"
          render={(_, { description, id }) => (
            <Link
              to={`/dashboard/settings/support/${id}`}
              className="flex flex-col justify-start text-left align-start truncate w-[200px] whitespace-nowrap">
              <p className="font-bold text-bold">{description}</p>
            </Link>
          )}
        />
        <Column
          title="Ticket No."
          key="ticket_id"
          dataIndex="ticket_id"
          render={(_, { ticket_id, id }) => (
            <Link
              to={`/dashboard/settings/support/${id}`}
              className="flex items-center gap-5 text-sm">
              <p className="text-regular">{ticket_id || "N/A"}</p>
            </Link>
          )}
        />
        <Column
          title="Date and Time"
          key="date"
          dataIndex="date"
          render={(_, { date, time, id }) => (
            <Link
              to={`/dashboard/settings/support/${id}`}
              className="flex items-center gap-5 text-sm">
              <p className="text-regular">
                {date || "N/A"} - {time || "N/A"}
              </p>
            </Link>
          )}
        />
        <Column
          title="Type"
          key="type"
          dataIndex="type"
          render={(_, { type, id }) => (
            <Link
              to={`/dashboard/settings/support/${id}`}
              className="flex items-center gap-5 text-sm">
              <p className="text-regular">{type || "Standard"}</p>
            </Link>
          )}
        />
        <Column
          title="Status"
          key="status"
          dataIndex="status"
          render={(_, { status, id }) => (
            <Link
              to={`/dashboard/settings/support/${id}`}
              className="flex items-center gap-1">
              <div
                className={`w-2 h-2 rounded-full ${
                  status === "Closed" ? "bg-danger" : "bg-primary"
                }`}
              />
              <p
                className={`text-regular font-bold ${
                  status === "Closed" ? "text-danger" : "text-primary"
                }`}>
                {status || "Open"}
              </p>
            </Link>
          )}
        />
        <Column
          title="Action"
          dataIndex="actions"
          key="actions"
          render={(_, record) => (
            <Button
              text="CLOSE"
              icon={<img src={DocumentCodeIcon} alt="Close" />}
              onClick={() => handleCloseClick(record)}
              iconPosition="left"
              className="border border-primary font-normal text-[11px] !px-3 !py-2 h-8" // Added size adjustments
              customBgColor="white"
              customTextColor="primary"
              disabled={record.status === "Closed"}
            />
          )}
        />
      </Table>

      <Delete
        isVisible={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onProceed={handleConfirmClose}
        header="Close Ticket"
        paragraph="Are you sure you want to close this support ticket? This action cannot be undone."
        proceedButtonText="Close Ticket"
        cancelButtonText="Cancel"
      />

      <Success
        isVisible={statusUpdateSuccess}
        onClose={handleCloseSuccess}
        header="Ticket Closed"
        paragraph="Support ticket has been successfully closed."
        buttonText="OK"
      />
    </>
  );
};

SupportHomeTable.propTypes = {
  setSelectedEvents: PropTypes.func,
  data: PropTypes.array,
  isLoading: PropTypes.bool,
};

export default SupportHomeTable;
